import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import functions from 'services/functions';

/**
 * Custom hook to check if a business ID exists in the firestore businesses collection.
 * * Does nothing if the business exists.
 * * Redirects the user to '/error' route if business does not exist or when any other error occurs.
 * * Calls a firebase cloud function.
 * @param {Object} params - The hook's input parameters.
 * @param {string} params.businessId - The business ID to check.
 * @returns {Object} The hook's output object.
 * @returns {boolean} output.isLoading - Indicates if the check is in progress.
 */
const useBusinessIdExists = ({ businessId }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const businessExists = httpsCallable(functions, 'businessExists');

  /**
   * Checks if the provided business ID exists and updates the state accordingly.
   * @async
   */

  useEffect(() => {
    if (!businessId) {
      throw new Error('No businessId passed to useBusinessIdExists hook');
    }

    let cancelled = false;

    const checkBusinessExists = async () => {
      setLoading(true);

      try {
        const response = await businessExists({ businessId });

        if (cancelled) return;

        if (!response.data.exists) {
          throw new Error('Business ID does not exist');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('businessExists check failed', error);
        navigate('/error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    checkBusinessExists();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line
  }, [businessId]);

  return { loading };
};

export default useBusinessIdExists;
